import {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
  type ReactNode,
} from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import type { FieldValidation } from '@rjsf/utils';
import {
  Grid,
  TextField,
  MenuItem,
  ListSubheader,
  CircularProgress,
  InputAdornment,
  Switch,
  Box,
  Typography,
} from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { YamlEditor } from '@openchoreo/backstage-plugin-react';
import { FormYamlToggle } from '@openchoreo/backstage-design-system';
import { CHOREO_ANNOTATIONS } from '@openchoreo/backstage-plugin-common';
import YAML from 'yaml';
import { useStyles } from './styles';
import {
  NamespaceSelectField,
  type NamespaceOption,
} from '../NamespaceEntityPicker';

export interface EnvironmentFormData {
  environment_name: string;
  namespace_name: string;
  displayName: string;
  description: string;
  dataPlaneRef: string;
  isProduction: boolean;
}

const DEFAULT_FORM_DATA: EnvironmentFormData = {
  environment_name: '',
  namespace_name: '',
  displayName: '',
  description: '',
  dataPlaneRef: '',
  isProduction: false,
};

const DEFAULT_ENVIRONMENT_TEMPLATE: {
  apiVersion: string;
  kind: string;
  metadata: {
    name: string;
    namespace: string;
    annotations: Record<string, string>;
  };
  spec: {
    dataPlaneRef: string | { kind: string; name: string };
    isProduction: boolean;
  };
} = {
  apiVersion: 'openchoreo.dev/v1alpha1',
  kind: 'Environment',
  metadata: {
    name: '',
    namespace: '',
    annotations: {
      'openchoreo.dev/display-name': '',
      'openchoreo.dev/description': '',
    },
  },
  spec: {
    dataPlaneRef: '',
    isProduction: false,
  },
};

/** Extract the last segment from an entity reference (e.g., "domain:default/name" -> "name") */
function extractName(entityRef: string): string {
  const parts = entityRef.split('/');
  return parts[parts.length - 1];
}

function formToYaml(data: EnvironmentFormData): string {
  const template = structuredClone(DEFAULT_ENVIRONMENT_TEMPLATE);
  template.metadata.name = data.environment_name;
  template.metadata.namespace = extractName(data.namespace_name);
  template.metadata.annotations['openchoreo.dev/display-name'] =
    data.displayName;
  template.metadata.annotations['openchoreo.dev/description'] =
    data.description;
  if (data.dataPlaneRef) {
    const isCluster = data.dataPlaneRef.startsWith('clusterdataplane:');
    template.spec.dataPlaneRef = {
      kind: isCluster ? 'ClusterDataPlane' : 'DataPlane',
      name: extractName(data.dataPlaneRef),
    };
  }
  template.spec.isProduction = data.isProduction;
  return YAML.stringify(template, { indent: 2 });
}

function yamlToForm(
  yamlContent: string,
  namespaces: Array<{ name: string; entityRef: string }>,
  dataplanes: Array<{
    name: string;
    entityRef: string;
    kind: 'DataPlane' | 'ClusterDataPlane';
  }>,
): Partial<EnvironmentFormData> {
  const parsed = YAML.parse(yamlContent);
  if (!parsed || typeof parsed !== 'object') return {};

  const namespaceName = parsed.metadata?.namespace || '';
  const rawDataPlaneRef = parsed.spec?.dataPlaneRef;

  // Extract dataplane name and kind from YAML value (string or object)
  let dataplaneName = '';
  let dataplaneKind: 'DataPlane' | 'ClusterDataPlane' = 'DataPlane';
  if (typeof rawDataPlaneRef === 'string') {
    dataplaneName = rawDataPlaneRef;
  } else if (rawDataPlaneRef && typeof rawDataPlaneRef === 'object') {
    dataplaneName = rawDataPlaneRef.name || '';
    dataplaneKind =
      rawDataPlaneRef.kind === 'ClusterDataPlane'
        ? 'ClusterDataPlane'
        : 'DataPlane';
  }

  // Try to match back to entity refs
  const matchedNamespace = namespaces.find(
    ns => extractName(ns.entityRef) === namespaceName,
  );
  const matchedDataplane = dataplanes.find(
    dp => dp.name === dataplaneName && dp.kind === dataplaneKind,
  );

  return {
    environment_name: parsed.metadata?.name || '',
    namespace_name: matchedNamespace?.entityRef || '',
    displayName:
      parsed.metadata?.annotations?.['openchoreo.dev/display-name'] || '',
    description:
      parsed.metadata?.annotations?.['openchoreo.dev/description'] || '',
    dataPlaneRef: matchedDataplane?.entityRef || '',
    isProduction: parsed.spec?.isProduction ?? false,
  };
}

/** Kubernetes name validation: lowercase alphanumeric and hyphens, must start/end with alphanumeric */
function isValidK8sName(name: string): boolean {
  return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(name);
}

export const EnvironmentFormWithYamlExtension = ({
  onChange,
  formData,
  rawErrors,
}: FieldExtensionComponentProps<EnvironmentFormData>) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);

  const [mode, setMode] = useState<'form' | 'yaml'>('form');
  const [yamlContent, setYamlContent] = useState('');
  const [yamlError, setYamlError] = useState<string | undefined>();

  const [namespaces, setNamespaces] = useState<NamespaceOption[]>([]);
  const [dataplanes, setDataplanes] = useState<
    Array<{
      name: string;
      entityRef: string;
      kind: 'DataPlane' | 'ClusterDataPlane';
    }>
  >([]);
  const [loadingDataplanes, setLoadingDataplanes] = useState(true);

  const initializedRef = useRef(false);
  const nsPreselectedRef = useRef(false);
  const formDataRef = useRef(formData);

  const [envNameDuplicateError, setEnvNameDuplicateError] = useState<
    string | null
  >(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);

  // Current form values - wrapped in useMemo to maintain stable reference
  const data: EnvironmentFormData = useMemo(
    () => ({ ...DEFAULT_FORM_DATA, ...formData }),
    [formData],
  );

  useEffect(() => {
    formDataRef.current = formData;
  });

  // Fetch Dataplane and ClusterDataplane entities filtered by selected namespace
  useEffect(() => {
    const nsName = data.namespace_name ? extractName(data.namespace_name) : '';

    const fetchDataplanes = async () => {
      setLoadingDataplanes(true);
      try {
        const results = await Promise.all([
          // Only fetch namespace-scoped dataplanes if a namespace is selected
          ...(nsName
            ? [
                catalogApi.getEntities({
                  filter: {
                    kind: 'Dataplane',
                    'metadata.namespace': nsName,
                  },
                }),
              ]
            : []),
          catalogApi.getEntities({ filter: { kind: 'ClusterDataplane' } }),
        ]);

        // When nsName is set, results[0] = namespace-scoped, results[1] = cluster-scoped
        // When nsName is empty, results[0] = cluster-scoped only
        const namespaceScoped = nsName ? results[0] : { items: [] };
        const clusterScoped = nsName ? results[1] : results[0];

        const nsList = namespaceScoped.items.map(entity => ({
          name: entity.metadata.name,
          entityRef: `dataplane:${entity.metadata.namespace || 'default'}/${
            entity.metadata.name
          }`,
          kind: 'DataPlane' as const,
        }));

        const clusterList = clusterScoped.items.map(entity => ({
          name: entity.metadata.name,
          entityRef: `clusterdataplane:${
            entity.metadata.namespace || 'openchoreo-cluster'
          }/${entity.metadata.name}`,
          kind: 'ClusterDataPlane' as const,
        }));

        const combined = [...clusterList, ...nsList];
        setDataplanes(combined);

        // Auto-select first dataplane if current selection is not in the list
        if (
          combined.length > 0 &&
          !combined.some(dp => dp.entityRef === data.dataPlaneRef)
        ) {
          onChange({ ...data, dataPlaneRef: combined[0].entityRef });
        }
      } catch {
        // ignore fetch errors
      } finally {
        setLoadingDataplanes(false);
      }
    };
    fetchDataplanes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.namespace_name]);

  // Initialize form data on mount if empty
  useEffect(() => {
    if (!initializedRef.current && !formData) {
      initializedRef.current = true;
      onChange(DEFAULT_FORM_DATA);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced duplicate check for environment name
  useEffect(() => {
    const envName = data.environment_name;
    const nsRef = data.namespace_name;
    const nsName = nsRef ? extractName(nsRef) : '';

    if (!envName || !isValidK8sName(envName) || !nsName) {
      setEnvNameDuplicateError(null);
      setCheckingDuplicate(false);
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {};
    }

    setCheckingDuplicate(true);

    const timeoutId = setTimeout(async () => {
      try {
        const { items } = await catalogApi.getEntities({
          filter: { kind: 'Environment' },
        });
        const exists = items.some(
          entity =>
            entity.metadata.name === envName &&
            entity.metadata.annotations?.[CHOREO_ANNOTATIONS.NAMESPACE] ===
              nsName &&
            !entity.metadata.annotations?.[
              CHOREO_ANNOTATIONS.DELETION_TIMESTAMP
            ],
        );
        if (exists) {
          setEnvNameDuplicateError(
            `An environment named "${envName}" already exists in namespace "${nsName}"`,
          );
        } else {
          setEnvNameDuplicateError(null);
        }
      } catch {
        setEnvNameDuplicateError(null);
      } finally {
        setCheckingDuplicate(false);
      }
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      setCheckingDuplicate(false);
    };
  }, [data.environment_name, data.namespace_name, catalogApi]);

  const updateField = useCallback(
    (field: keyof EnvironmentFormData, value: string | boolean) => {
      const updated = { ...data, [field]: value };
      onChange(updated);
    },
    [data, onChange],
  );

  const handleModeChange = useCallback(
    (newMode: 'form' | 'yaml') => {
      if (newMode === mode) return;

      if (newMode === 'yaml') {
        // Form -> YAML: generate YAML from current form values
        setYamlContent(formToYaml(data));
        setYamlError(undefined);
      } else {
        // YAML -> Form: parse YAML and update form
        try {
          const parsed = yamlToForm(yamlContent, namespaces, dataplanes);
          onChange({ ...data, ...parsed });
          setYamlError(undefined);
        } catch (err) {
          setYamlError(`Failed to parse YAML: ${err}`);
          return; // Don't switch if YAML is invalid
        }
      }
      setMode(newMode);
    },
    [mode, data, yamlContent, namespaces, dataplanes, onChange],
  );

  const handleYamlChange = useCallback(
    (content: string) => {
      setYamlContent(content);
      try {
        YAML.parse(content);
        setYamlError(undefined);
        // Parse and update form data so submission always has current values
        const parsed = yamlToForm(content, namespaces, dataplanes);
        onChange({ ...data, ...parsed });
      } catch (err) {
        setYamlError(`YAML parse error: ${err}`);
      }
    },
    [namespaces, dataplanes, data, onChange],
  );

  return (
    <div>
      {/* Toggle */}
      <div className={classes.toggleContainer}>
        <FormYamlToggle value={mode} onChange={handleModeChange} />
      </div>

      {mode === 'form' ? (
        <div className={classes.formContainer}>
          <Grid container spacing={2}>
            {/* Namespace */}
            <Grid item xs={12} sm={6}>
              <NamespaceSelectField
                value={data.namespace_name}
                onChange={v => updateField('namespace_name', v)}
                label="Namespace"
                helperText="Namespace where the environment will be created"
                required
                onNamespacesLoaded={ns => {
                  setNamespaces(ns);
                  if (!nsPreselectedRef.current && ns.length > 0) {
                    const current = formDataRef.current;
                    if (!current?.namespace_name) {
                      nsPreselectedRef.current = true;
                      const defaultNs = ns.find(n => n.name === 'default');
                      onChange({
                        ...DEFAULT_FORM_DATA,
                        ...current,
                        namespace_name: (defaultNs ?? ns[0]).entityRef,
                      });
                    }
                  }
                }}
              />
            </Grid>

            {/* Environment Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Environment Name"
                value={data.environment_name}
                onChange={e => updateField('environment_name', e.target.value)}
                fullWidth
                variant="outlined"
                required
                error={
                  (!!data.environment_name &&
                    !isValidK8sName(data.environment_name)) ||
                  !!envNameDuplicateError
                }
                helperText={
                  data.environment_name &&
                  !isValidK8sName(data.environment_name)
                    ? 'Must be lowercase alphanumeric with hyphens, starting and ending with alphanumeric'
                    : envNameDuplicateError ||
                      'Unique name for your environment (must be a valid Kubernetes name)'
                }
                InputProps={{
                  endAdornment: checkingDuplicate ? (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ) : undefined,
                }}
              />
            </Grid>

            {/* Data Plane */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Data Plane"
                value={data.dataPlaneRef}
                onChange={e => updateField('dataPlaneRef', e.target.value)}
                fullWidth
                variant="outlined"
                required
                disabled={loadingDataplanes || !data.namespace_name}
                helperText={
                  !data.namespace_name
                    ? 'Select a namespace first'
                    : 'Select the data plane cluster for workloads in this environment'
                }
                SelectProps={{
                  renderValue: value => {
                    const selected = dataplanes.find(
                      dp => dp.entityRef === value,
                    );
                    if (!selected) return value as string;
                    const typeLabel =
                      selected.kind === 'ClusterDataPlane'
                        ? 'Cluster'
                        : 'Namespace';
                    return `${selected.name} (${typeLabel})`;
                  },
                }}
                InputProps={{
                  endAdornment: loadingDataplanes ? (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ) : undefined,
                }}
              >
                {(() => {
                  const clusterDps = dataplanes.filter(
                    dp => dp.kind === 'ClusterDataPlane',
                  );
                  const namespaceDps = dataplanes.filter(
                    dp => dp.kind === 'DataPlane',
                  );
                  const items: ReactNode[] = [];

                  if (clusterDps.length > 0) {
                    items.push(
                      <ListSubheader key="header-cluster" disableSticky>
                        Cluster Data Planes
                      </ListSubheader>,
                    );
                    clusterDps.forEach(dp => {
                      items.push(
                        <MenuItem key={dp.entityRef} value={dp.entityRef}>
                          {dp.name}
                        </MenuItem>,
                      );
                    });
                  }

                  if (namespaceDps.length > 0) {
                    items.push(
                      <ListSubheader key="header-namespace" disableSticky>
                        Namespace Data Planes
                      </ListSubheader>,
                    );
                    namespaceDps.forEach(dp => {
                      items.push(
                        <MenuItem key={dp.entityRef} value={dp.entityRef}>
                          {dp.name}
                        </MenuItem>,
                      );
                    });
                  }

                  return items;
                })()}
              </TextField>
            </Grid>

            {/* Display Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Display Name"
                value={data.displayName}
                onChange={e => updateField('displayName', e.target.value)}
                fullWidth
                variant="outlined"
                helperText="A human-readable display name for the environment"
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={data.description}
                onChange={e => updateField('description', e.target.value)}
                fullWidth
                variant="outlined"
                helperText="Describe what this environment is for (e.g., Development, Staging, Production)"
              />
            </Grid>

            {/* Production Switch */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center">
                <Box style={{ marginRight: 16 }}>
                  <Typography variant="body1">
                    Production Environment
                  </Typography>
                </Box>
                <Switch
                  checked={data.isProduction}
                  onChange={e => updateField('isProduction', e.target.checked)}
                  color="primary"
                />
              </Box>
              <Typography variant="body2" color="textSecondary">
                Mark this as a production environment.
              </Typography>
            </Grid>
          </Grid>
        </div>
      ) : (
        <div>
          <div className={classes.helpText}>
            <span>
              Edit the Environment CR YAML directly. For available fields and
              configuration options, see the{' '}
              <a
                className={classes.helpLink}
                href="https://openchoreo.dev/docs/reference/api/platform/environment/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Environment documentation
              </a>
              .
            </span>
          </div>
          <div className={classes.yamlContainer}>
            <YamlEditor
              content={yamlContent}
              onChange={handleYamlChange}
              errorText={yamlError}
            />
          </div>
        </div>
      )}

      {rawErrors && rawErrors.length > 0 && (
        <div className={classes.errorText}>{rawErrors.join(', ')}</div>
      )}
    </div>
  );
};

export const EnvironmentFormWithYamlSchema = {
  returnValue: {
    type: 'object' as const,
    properties: {
      environment_name: { type: 'string' as const },
      namespace_name: { type: 'string' as const },
      displayName: { type: 'string' as const },
      description: { type: 'string' as const },
      dataPlaneRef: { type: 'string' as const },
      isProduction: { type: 'boolean' as const },
    },
  },
};

export const environmentFormWithYamlValidation = (
  value: EnvironmentFormData,
  validation: FieldValidation,
) => {
  if (!value?.environment_name || value.environment_name.trim() === '') {
    validation.addError('Environment name is required');
  } else if (!isValidK8sName(value.environment_name)) {
    validation.addError(
      'Environment name must be lowercase alphanumeric with hyphens, starting and ending with alphanumeric',
    );
  }
  if (!value?.namespace_name || value.namespace_name.trim() === '') {
    validation.addError('Namespace is required');
  }
  if (!value?.dataPlaneRef || value.dataPlaneRef.trim() === '') {
    validation.addError('Data Plane is required');
  }
};
